/**
 * Formatage partagé des écrans « Analyse Portefeuille » (desktop et mobile).
 *
 * POURQUOI
 * `toLocaleString()` sans locale suivait la langue du navigateur (« 1,234,567.8 » en anglais)
 * et les montants n'étaient pas arrondis : des décimales apparaissaient sur des francs CFA.
 * Les deux écrans écrivaient aussi l'unité différemment (« FCFA » / « F »).
 */
const LOCALE = 'fr-FR';

export const POSITIF = '#10b981';
export const NEGATIF = '#ef4444';

/** « 1 234 568 F CFA » — montants en XOF, entiers. */
export const fcfa = (montant: number | null | undefined): string =>
  montant === null || montant === undefined || !Number.isFinite(montant)
    ? '—'
    : `${Math.round(montant).toLocaleString(LOCALE)} F CFA`;

/** « +1 234 568 F CFA » — variation signée. */
export const fcfaSigne = (montant: number | null | undefined): string =>
  montant === null || montant === undefined || !Number.isFinite(montant)
    ? '—'
    : `${Math.round(montant) > 0 ? '+' : ''}${fcfa(montant)}`;

/** « 1 234 568 » — montant entier sans unité (cellules de tableau). */
export const entier = (montant: number | null | undefined): string =>
  montant === null || montant === undefined || !Number.isFinite(montant)
    ? '—'
    : Math.round(montant).toLocaleString(LOCALE);

/** « 4 512 » ou « 33 511,90 » — cours et PRU : deux décimales dès qu'il y a des centimes. */
export const cours = (montant: number | null | undefined): string => {
  if (montant === null || montant === undefined || !Number.isFinite(montant)) return '—';
  const decimales = Math.round(montant * 100) % 100 === 0 ? 0 : 2;
  return montant.toLocaleString(LOCALE, {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
};

/** « +12,50 % » — pourcentage signé. */
export const pctSigne = (valeur: number | null | undefined, decimales = 2): string =>
  valeur === null || valeur === undefined || !Number.isFinite(valeur)
    ? '—'
    : `${valeur > 0 ? '+' : ''}${valeur.toLocaleString(LOCALE, {
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales,
      })} %`;

/** « 12,5 % » — pourcentage non signé (poids, volatilité, rendement). */
export const pct = (valeur: number | null | undefined, decimales = 1): string =>
  valeur === null || valeur === undefined || !Number.isFinite(valeur)
    ? '—'
    : `${valeur.toLocaleString(LOCALE, {
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales,
      })} %`;

/** « 13,33 » — ratio sans unité. */
export const ratio = (valeur: number | null | undefined): string =>
  valeur === null || valeur === undefined || !Number.isFinite(valeur)
    ? '—'
    : valeur.toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Couleur d'une variation ; neutre quand la valeur est absente. */
export const couleur = (valeur: number | null | undefined): string =>
  valeur === null || valeur === undefined || !Number.isFinite(valeur)
    ? 'var(--text-muted)'
    : valeur >= 0
      ? POSITIF
      : NEGATIF;

/** « 23 octobre 2026 » à partir d'une date ISO `YYYY-MM-DD`, sans décalage de fuseau. */
export const dateLongue = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [a, m, j] = iso.slice(0, 10).split('-').map(Number);
  return new Date(a, m - 1, j).toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/** Message affiché à l'abonné quand l'analyse ne se charge pas — jamais le texte brut de l'API. */
export const messageErreur = (status: number): string =>
  status === 401 || status === 403
    ? 'Votre session a expiré. Reconnectez-vous pour afficher votre analyse.'
    : "L'analyse de votre portefeuille n'a pas pu être chargée. Réessayez dans quelques instants.";
